import { useState } from 'react'
import { Space, Typography, Form, Row, Col, Checkbox, notification } from 'antd'
import _ from 'lodash'
import { useAsync } from 'react-use'

import Page from 'components/Page'
import Card from 'components/Card'
import Button from 'components/Button'
import Modal from 'components/Modal'
import AllActiveInactive from 'components/AllActiveInactive'
import SearchOrganization from 'components/SearchOrganization'
import TableOrganization from 'components/TableOrganization'

import { useTranslate } from 'hooks/translate'

import { getOrganizationListApi, updateOrganizationByIdApi } from 'services/organization'

import { convertOrganizationalStructureToFilters } from 'utils/organization'

import Styled from './styled'

export default function PageOrganization () {
  const { t } = useTranslate()

  const [editMode, setEditMode] = useState(false)
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [oldDataSource, setOldDataSource] = useState([])
  const [currentDataSource, setCurrentDataSource] = useState([])
  const [status, setStatus] = useState('all') // all, active, inactive
  const [loading, setLoading] = useState(false)
  const [dataForUpdate, setDataForUpdate] = useState({})
  const [submitting, setSubmitting] = useState(false)
  const [orgFilters, setOrgFilters] = useState({
    company_id: null,
    group_id: null,
    bu_id: null,
    division_id: null,
    department_id: null,
    section_id: null,
    org_with_risk_co: null,
  })
  const [selectedRowKeys, setSelectedRowKeys] = useState([])

  useAsync(async () => {
    const newSelectedRowKeys = []

    _.forEach(currentDataSource, (company) => {
      if (company.status.value) {
        newSelectedRowKeys.push(company.key)
      }
      _.forEach(company?.groups, (group) => {
        if (group.status.value) {
          newSelectedRowKeys.push(group.key)
        }
        _.forEach(group?.bus, (bu) => {
          if (bu.status.value) {
            newSelectedRowKeys.push(bu.key)
          }
          _.forEach(bu?.divisions, (division) => {
            if (division.status.value) {
              newSelectedRowKeys.push(division.key)
            }
            _.forEach(division?.departments, (department) => {
              if (department.status.value) {
                newSelectedRowKeys.push(department.key)
              }
              _.forEach(department?.sections, (section) => {
                if (section.status.value) {
                  newSelectedRowKeys.push(section.key)
                }
              })
            })
          })
        })
      })
    })

    setSelectedRowKeys(newSelectedRowKeys)
  }, [currentDataSource])

  const _list = async () => {
    if (loading) return

    const params = {
      ...orgFilters,
    }

    if (status !== 'all') {
      params.is_active = status === 'active'
    }

    setLoading(true)

    const res = await getOrganizationListApi(params)

    setLoading(false)

    if (res.status === 'error') {
      notification.error({
        message: 'Failed',
        description: res.message,
      })

      return
    }

    setOldDataSource(res.data)
    setCurrentDataSource(res.data)
  }

  useAsync(async () => {
    _list()
  }, [status, orgFilters])

  const columns = [
    {
      title: 'Code',
      dataIndex: 'code',
      key: 'code',
      sorter: (a, b) => a.code.localeCompare(b.code),
    },
    {
      title: 'ABBR',
      dataIndex: 'abbr',
      key: 'abbr',
      sorter: (a, b) => a.abbr.localeCompare(b.abbr),
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: 'Risk Co Status',
      dataIndex: ['status', 'label'],
      key: 'status',
      sorter: (a, b) => {
        return a.status.label.localeCompare(b.status.label)
      },
    },
  ]

  const getChangeMessages = () => {
    const result = ''
    return result
  }

  const changeMessages = getChangeMessages()

  const onSelectRow = ({ level, record, selected }) => {
    if (selected) {
      setSelectedRowKeys([...selectedRowKeys, record.key])
    } else {
      setSelectedRowKeys(
        _.filter(selectedRowKeys, (selectedRowKey) => selectedRowKey !== record.key)
      )
    }

    const newDataForUpdate = { ...dataForUpdate }

    newDataForUpdate[record.key] = {
      level,
      id: record.id,
      is_pool: selected,
      record,
    }

    setDataForUpdate(newDataForUpdate)
  }

  const _update = async () => {
    if (submitting) return

    setSubmitting(true)

    await Promise.all(
      _.map(dataForUpdate, async (v) => {
        const res = await updateOrganizationByIdApi({
          level: v.level,
          id: v.id,
          is_pool: v.is_pool,
        })

        if (res.status === 'error') {
          notification.error({
            message: 'Failed',
            description: res.message ? (
              <Row>
                {res.message.split('\n').map((item) => (
                  <Col span={24}>{item}</Col>
                ))}
              </Row>
            ) : (
              ''
            ),
          })

          return
        }

        return res
      })
    )

    setSubmitting(false)

    _list()

    setEditMode(false)
    setShowConfirmModal(false)
    setDataForUpdate({})
  }

  return (
    <Styled className='page page-organization'>
      <Page title={t('Organization')} showSearch={false}>
        <Space size='middle' direction='vertical' style={{ width: '100%' }}>
          <Form
            layout='vertical'
            name='search'
            onFinish={(values) => {
              const nextOrgFilters = convertOrganizationalStructureToFilters(values.search)
              setOrgFilters({
                ...nextOrgFilters,
                org_with_risk_co: values?.isOnlyOrgWithRiskCo,
              })
            }}
          >
            <Row gutter={8}>
              <Col span={20}>
                <Form.Item
                  label={t('Organizational Structure')}
                  name='search'
                  style={{ marginBottom: 0 }}
                >
                  <SearchOrganization />
                </Form.Item>
              </Col>

              <Col>
                <Form.Item label='&nbsp;' style={{ marginBottom: 0 }}>
                  <Button type='primary' htmlType='submit'>
                    {t('Search')}
                  </Button>
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={8}>
              <Col span={20}>
                <Row justify='end'>
                  <Col>
                    <Form.Item valuePropName='checked' name='isOnlyOrgWithRiskCo'>
                      <Checkbox>{t('Only Org with Risk Co')}</Checkbox>
                    </Form.Item>
                  </Col>
                </Row>
              </Col>

              <Col></Col>
            </Row>
          </Form>

          <Card
            title={t('Organization Mapping')}
            titleExtra={
              editMode ? (
                <Space>
                  <Button type='primary' onClick={() => setShowConfirmModal(true)}>
                    {t('Save')}
                  </Button>
                  <Button
                    onClick={() => {
                      setEditMode(false)
                      setCurrentDataSource(oldDataSource)
                      setDataForUpdate({})
                    }}
                  >
                    {t('Cancel')}
                  </Button>
                </Space>
              ) : (
                <Button type='primary' onClick={() => setEditMode(true)}>
                  {t('Edit Mode')}
                </Button>
              )
            }
          >
            <Row justify='end'>
              <Col xs={24} sm={8} lg={4}>
                <AllActiveInactive value={status} onChange={(value) => setStatus(value)} />
              </Col>
            </Row>
            <TableOrganization
              disabledNA
              loading={loading}
              columns={columns}
              dataSource={currentDataSource}
              selectedRowKeys={selectedRowKeys}
              editMode={editMode}
              scroll={{ x: 800, y: 600 }}
              onSelectRow={onSelectRow}
            />
          </Card>
        </Space>
      </Page>

      <Modal
        visible={showConfirmModal}
        title={t('Confirm changes')}
        onCancel={() => setShowConfirmModal(false)}
        footer={[
          <Button
            loading={submitting}
            key='save'
            type='primary'
            onClick={async () => {
              _update()
            }}
          >
            {t('Save')}
          </Button>,
        ]}
      >
        <div>
          <Typography.Text>{t('You have change is_pool flags as below')}</Typography.Text>
          <Typography.Paragraph>
            <ol>
              {_.map(changeMessages, (changeMessage, index) => {
                return (
                  <li key={index}>
                    <Typography.Text>{changeMessage}</Typography.Text>
                  </li>
                )
              })}
            </ol>
          </Typography.Paragraph>
        </div>
      </Modal>
    </Styled>
  )
}
